/**
 * AdminEmailTemplatesPanel — Registry + editor de templates de email FM.
 *
 * Features:
 *  - Lista de templates con filtro por categoría/búsqueda
 *  - Detalle del template seleccionado
 *  - Preview HTML completo en modal (iframe full height)
 *  - Editor HTML: ver código, editar inline, copiar al portapapeles
 *  - Pegar HTML externo y previsualizar en vivo
 *  - Test send con el HTML actual (template o editado)
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { FM_EMAIL_TEMPLATES, EMAIL_CATEGORY_LABELS, type EmailTemplateDef, type EmailTemplateCategory } from "@/lib/email-template-registry";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { apiSendJson } from "@/lib/sm-api";
import { toast } from "sonner";
import { Copy, Mail, Code2, Eye, X, RefreshCw, ExternalLink, Clipboard } from "lucide-react";

const CATEGORIES: EmailTemplateCategory[] = ["auth", "onboarding", "billing", "commerce", "support", "ops", "marketing"];

// ── Helpers ──────────────────────────────────────────────────────────────────

async function fetchHtmlPreview(templateKey: string, customHtml?: string): Promise<{ html: string; subject: string }> {
  const d = await apiSendJson<{ html?: string; subject?: string }>("/api/admin/email-test", "POST", {
    templateKey,
    previewOnly: true,
    ...(customHtml ? { customHtml } : {}),
  });
  return { html: d.html ?? "<p>(sin contenido)</p>", subject: d.subject ?? "" };
}

// ── HTML Editor/Preview Modal ─────────────────────────────────────────────────

function HtmlEditorModal({
  template,
  onClose,
  testTo,
}: {
  template: EmailTemplateDef;
  onClose: () => void;
  testTo: string;
}) {
  const [mode, setMode] = useState<"preview" | "code">("preview");
  const [html, setHtml] = useState<string>("");
  const [subject, setSubject] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendTo, setSendTo] = useState(testTo || "fernandez.gaston.e@gmail.com");
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Carga inicial: HTML sample del template
  const loadTemplate = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchHtmlPreview(template.key);
      setHtml(res.html);
      setSubject(res.subject);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo cargar el HTML del template");
    } finally {
      setLoading(false);
    }
  }, [template.key]);

  useEffect(() => { void loadTemplate(); }, [loadTemplate]);

  // Actualizar iframe cuando cambia el HTML
  useEffect(() => {
    if (iframeRef.current && html) {
      iframeRef.current.srcdoc = html;
    }
  }, [html]);

  const copyHtml = () => {
    void navigator.clipboard.writeText(html).then(() => toast.success("HTML copiado al portapapeles"));
  };

  const pasteHtml = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text.trim()) {
        setHtml(text.trim());
        toast.success("HTML pegado — preview actualizado");
      }
    } catch {
      toast.error("No se pudo leer el portapapeles");
    }
  };

  const resetToTemplate = () => { void loadTemplate(); };

  const openInBrowser = () => {
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const sendTest = async () => {
    if (!sendTo.trim()) { toast.error("Ingresá un email de destino"); return; }
    setSending(true);
    try {
      await apiSendJson("/api/admin/email-test", "POST", {
        to: sendTo.trim(),
        templateKey: template.key,
        customHtml: html,
        customSubject: subject,
      });
      toast.success(`Email enviado a ${sendTo}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo enviar el email");
    } finally {
      setSending(false);
    }
  };

  return (
    // Overlay
    <div className="fixed inset-0 z-50 flex flex-col bg-background" role="dialog" aria-modal>
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-2 gap-3 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-semibold text-sm truncate">{template.label}</span>
          <span className="font-mono text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded truncate hidden sm:inline">{template.key}</span>
        </div>

        {/* Mode toggle */}
        <div className="flex items-center gap-1 rounded-md border p-0.5 flex-shrink-0">
          <button
            onClick={() => setMode("preview")}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors ${mode === "preview" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
          >
            <Eye className="h-3.5 w-3.5" /> Preview
          </button>
          <button
            onClick={() => setMode("code")}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors ${mode === "code" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
          >
            <Code2 className="h-3.5 w-3.5" /> HTML
          </button>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <Button size="sm" variant="outline" onClick={copyHtml} title="Copiar HTML">
            <Copy className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Copiar HTML</span>
          </Button>
          <Button size="sm" variant="outline" onClick={pasteHtml} title="Pegar HTML del portapapeles">
            <Clipboard className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Pegar HTML</span>
          </Button>
          <Button size="sm" variant="outline" onClick={resetToTemplate} title="Resetear al template original" disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button size="sm" variant="outline" onClick={openInBrowser} title="Abrir en nueva pestaña">
            <ExternalLink className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose} title="Cerrar">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Subject line */}
      <div className="flex items-center gap-2 border-b px-4 py-1.5 flex-shrink-0 bg-muted/30">
        <span className="text-xs text-muted-foreground font-medium">Asunto:</span>
        <input
          className="flex-1 bg-transparent text-sm outline-none"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Asunto del email…"
        />
      </div>

      {/* Content: preview ó editor */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">Cargando template…</div>
        ) : mode === "preview" ? (
          <iframe
            ref={iframeRef}
            title="email-preview"
            sandbox="allow-same-origin"
            className="w-full h-full border-0 bg-white"
            srcDoc={html}
          />
        ) : (
          <textarea
            className="w-full h-full resize-none font-mono text-xs p-4 bg-background outline-none border-0"
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            spellCheck={false}
            placeholder="Pegá o editá el HTML aquí…"
          />
        )}
      </div>

      {/* Footer: test send */}
      <div className="border-t px-4 py-3 flex flex-wrap items-center gap-3 flex-shrink-0 bg-muted/20">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium flex-shrink-0">
          <Mail className="h-3.5 w-3.5" /> Test send:
        </div>
        <Input
          className="h-8 text-sm max-w-xs"
          value={sendTo}
          onChange={(e) => setSendTo(e.target.value)}
          placeholder="email@ejemplo.com"
          type="email"
        />
        <Button size="sm" onClick={() => void sendTest()} disabled={sending}>
          {sending ? "Enviando…" : "Enviar con este HTML"}
        </Button>
        <span className="text-xs text-muted-foreground">Usa el HTML del editor (template original o editado/pegado)</span>
      </div>
    </div>
  );
}

// ── Panel principal ───────────────────────────────────────────────────────────

export function AdminEmailTemplatesPanel({ userEmail }: { userEmail?: string }) {
  const [filter, setFilter] = useState("");
  const [category, setCategory] = useState<string>("all");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [testTo, setTestTo] = useState(userEmail ?? "fernandez.gaston.e@gmail.com");
  const [editorOpen, setEditorOpen] = useState(false);
  const [quickSending, setQuickSending] = useState(false);

  const rows = useMemo(() => {
    return FM_EMAIL_TEMPLATES.filter((t) => {
      if (category !== "all" && t.category !== category) return false;
      if (!filter.trim()) return true;
      const q = filter.toLowerCase();
      return t.key.includes(q) || t.label.toLowerCase().includes(q) || t.trigger.toLowerCase().includes(q);
    });
  }, [filter, category]);

  const selected: EmailTemplateDef | undefined = selectedKey
    ? (FM_EMAIL_TEMPLATES.find((r) => r.key === selectedKey) ?? rows[0])
    : rows[0];

  const grouped = useMemo(() => {
    const byCategory = new Map<EmailTemplateCategory, EmailTemplateDef[]>();
    for (const t of rows) {
      const arr = byCategory.get(t.category) ?? [];
      arr.push(t);
      byCategory.set(t.category, arr);
    }
    return Array.from(byCategory.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);

  // Quick send (sin abrir el editor)
  const quickSend = async () => {
    if (!testTo.trim()) { toast.error("Ingresá un email de destino"); return; }
    if (!selected?.key) return;
    setQuickSending(true);
    try {
      await apiSendJson("/api/admin/email-test", "POST", {
        to: testTo.trim(),
        templateKey: selected.key,
      });
      toast.success(`Test enviado a ${testTo}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo enviar");
    } finally {
      setQuickSending(false);
    }
  };

  return (
    <>
      {/* Editor modal (full screen) */}
      {editorOpen && selected && (
        <HtmlEditorModal
          template={selected}
          onClose={() => setEditorOpen(false)}
          testTo={testTo}
        />
      )}

      <div className="space-y-4">
        {/* Filtros */}
        <div className="flex flex-wrap gap-2">
          <Input
            placeholder="Buscar key, label, trigger…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="max-w-xs"
          />
          <select
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="all">Todas las categorías</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div className="md:grid md:grid-cols-[1fr_320px] md:items-start md:gap-4 flex flex-col gap-4">
          {/* Lista agrupada por categoría — cards en mobile, tabla en desktop */}
          <div className="min-w-0 space-y-4">
            {grouped.map(([cat, catRows]) => (
              <div key={cat} className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {EMAIL_CATEGORY_LABELS[cat]} <span className="font-normal">({catRows.length})</span>
                </h3>

                <div className="flex flex-col gap-2 md:hidden">
                  {catRows.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setSelectedKey(t.key)}
                      onDoubleClick={() => { setSelectedKey(t.key); setEditorOpen(true); }}
                      className={`rounded-xl border p-3 text-left transition-colors ${selected?.key === t.key ? "bg-primary/5 border-primary" : "hover:bg-muted/40"}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="font-medium text-sm">{t.label}</span>
                        <Badge variant={t.status === "active" ? "default" : "secondary"} className="shrink-0">{t.status}</Badge>
                      </div>
                      <p className="mt-1 font-mono text-xs text-muted-foreground break-all">{t.key}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{t.trigger}</p>
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        {t.scope === "platform"
                          ? "platform"
                          : `${t.scope} · ${t.minPlan ?? "starter"}${t.requiredFlag ? ` · ${t.requiredFlag}` : ""}`}
                      </p>
                    </button>
                  ))}
                </div>
                <div className="hidden w-full rounded-xl border overflow-hidden md:block">
                  <Table className="w-full table-fixed">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[18%]">Key</TableHead>
                        <TableHead className="w-[22%]">Label</TableHead>
                        <TableHead className="w-[22%]">Trigger</TableHead>
                        <TableHead className="w-[10%]">Scope</TableHead>
                        <TableHead className="w-[18%]">Plan / Flag</TableHead>
                        <TableHead className="w-[10%]">Estado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {catRows.map((t) => (
                        <TableRow
                          key={t.key}
                          className={`cursor-pointer transition-colors ${selected?.key === t.key ? "bg-primary/5 border-l-2 border-l-primary" : "hover:bg-muted/40"}`}
                          onClick={() => setSelectedKey(t.key)}
                          onDoubleClick={() => { setSelectedKey(t.key); setEditorOpen(true); }}
                        >
                          <TableCell className="font-mono text-xs truncate" title={t.key}>{t.key}</TableCell>
                          <TableCell className="font-medium truncate" title={t.label}>{t.label}</TableCell>
                          <TableCell className="text-xs text-muted-foreground truncate" title={t.trigger}>{t.trigger}</TableCell>
                          <TableCell className="text-xs">{t.scope}</TableCell>
                          <TableCell className="text-[11px] text-muted-foreground">
                            {t.scope === "platform"
                              ? "—"
                              : `${t.minPlan ?? "starter"}${t.requiredFlag ? ` · ${t.requiredFlag}` : ""}`}
                          </TableCell>
                          <TableCell>
                            <Badge variant={t.status === "active" ? "default" : "secondary"}>{t.status}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ))}
          </div>

          {/* Panel lateral: detalle + acciones — sticky en desktop, visible sin scroll completo */}
          {selected ? (
            <div className="space-y-4 rounded-xl border p-4 w-full md:sticky md:top-4">
              <div>
                <h3 className="font-semibold text-base">{selected.label}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">{selected.category} · {selected.provider}</p>
              </div>

              <dl className="grid gap-2 text-sm">
                <div className="grid grid-cols-[80px_1fr] gap-1">
                  <dt className="text-muted-foreground text-xs pt-0.5">Key</dt>
                  <dd className="font-mono text-xs break-all">{selected.key}</dd>
                </div>
                <div className="grid grid-cols-[80px_1fr] gap-1">
                  <dt className="text-muted-foreground text-xs pt-0.5">Trigger</dt>
                  <dd className="text-xs">{selected.trigger}</dd>
                </div>
                <div className="grid grid-cols-[80px_1fr] gap-1">
                  <dt className="text-muted-foreground text-xs pt-0.5">Owner</dt>
                  <dd className="text-xs">{selected.owner}</dd>
                </div>
                <div className="grid grid-cols-[80px_1fr] gap-1">
                  <dt className="text-muted-foreground text-xs pt-0.5">Fuente</dt>
                  <dd className="font-mono text-[10px] break-all text-muted-foreground">{selected.sourceFile}<br />→ {selected.sourceFunction}</dd>
                </div>
                <div className="grid grid-cols-[80px_1fr] gap-1">
                  <dt className="text-muted-foreground text-xs pt-0.5">Variables</dt>
                  <dd className="text-xs font-mono">{selected.variables.join(", ")}</dd>
                </div>
              </dl>

              {/* Botón principal: abrir editor */}
              <Button
                className="w-full"
                onClick={() => setEditorOpen(true)}
              >
                <Eye className="mr-2 h-4 w-4" /> Ver / Editar HTML completo
              </Button>

              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => {
                  void navigator.clipboard.writeText(`${selected.sourceFile}#${selected.sourceFunction}`);
                  toast.success("Ruta copiada");
                }}
              >
                <Copy className="mr-2 h-3.5 w-3.5" /> Copiar referencia código
              </Button>

              {/* Quick send */}
              <div className="border-t pt-3 space-y-2">
                <Label className="text-xs text-muted-foreground">Envío rápido (HTML del template)</Label>
                <Input
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="email@…"
                  type="email"
                  className="h-8 text-sm"
                />
                <Button
                  className="w-full"
                  variant="outline"
                  size="sm"
                  onClick={() => void quickSend()}
                  disabled={quickSending}
                >
                  <Mail className="mr-2 h-3.5 w-3.5" />
                  {quickSending ? "Enviando…" : "Probar template"}
                </Button>
                <p className="text-[10px] text-muted-foreground">Para usar HTML editado → abrí el editor completo</p>
              </div>
            </div>
          ) : null}
        </div>

        <p className="text-xs text-muted-foreground">
          💡 Doble clic en un template para abrir el editor directamente. El HTML vive en <code className="text-[10px]">src/lib/email.ts</code>.
        </p>
      </div>
    </>
  );
}
